import React, { Component, Fragment } from 'react'
import classNames from 'classnames'
import { Link, withRouter } from 'react-router-dom'
import { Container, Icon } from '@scentregroup/ui'
import PropTypes from 'prop-types'
import Search from '../search'
import { throttle } from '../../helpers/'
import ExternalLink from '../external-link'
import getGiftcardUrl from '../../helpers/get-giftcard-url'
import analyticsUtils from '../../helpers/analytics-utils'
import Menu from './menu'
import style from './index.scss'
import { CentreType, NavigationMenuType } from './types'
import OpeningHoursBanner from './opening-hours-banner'

const THROTTLE_SCROLL = 4

let headerStyle = classNames(style.stickHeader, style.header)

class Header extends Component {
  static toggleStickiness = isSticky => {
    if (isSticky) {
      headerStyle = classNames(style.stickHeader, style.header)
    } else {
      headerStyle = style.header
    }
  }

  constructor(props) {
    super(props)
    this.onScroll = throttle(this.onScroll, THROTTLE_SCROLL)
    this.state = {
      sticky: false,
    }
  }

  componentDidMount() {
    window.addEventListener('scroll', this.onScroll)
    this.setFixedHeight()
    this.addCollapsableScrollHandler()
  }

  componentDidUpdate(prevProps) {
    const { isCentreHome } = this.props
    if (isCentreHome !== prevProps.isCentreHome) {
      this.setFixedHeight()
      if (isCentreHome) {
        this.addCollapsableScrollHandler()
      } else {
        this.removeCollapsableScrollHandler()
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.onScroll)
    this.removeCollapsableScrollHandler()
  }

  setFixedHeight = () => {
    if (this.wrapper) {
      this.wrapper.style.minHeight =
        this.props.isCentreHome && this.openHoursCollapsible
          ? `${this.wrapper.offsetHeight}px`
          : ''
    }
  }

  addCollapsableScrollHandler() {
    // This is the only way I can to get the offset height
    // regardless of the starting scroll position
    if (
      this.props.isCentreHome &&
      this.openHoursCollapsible &&
      this.wrapper &&
      this.headerNormalPosition
    ) {
      const height = this.openHoursCollapsible.offsetHeight

      const offset = Math.abs(
        document.documentElement.getBoundingClientRect().top -
          this.headerNormalPosition.getBoundingClientRect().top +
          this.headerNormalPosition.getBoundingClientRect().height
      )

      this.collapsableOnScroll = this.handleScroll(offset, height)
      document.addEventListener('scroll', this.collapsableOnScroll)
    }
  }

  removeCollapsableScrollHandler() {
    if (this.collapsableOnScroll) {
      window.removeEventListener('scroll', this.collapsableOnScroll)
      this.collapsableOnScroll = null
    }
  }

  handleScroll = (scrollOffset, height) => e => {
    if (this.openHoursCollapsible) {
      /* eslint-disable no-magic-numbers */
      const currentHeight = Math.min(
        Math.max(height + (scrollOffset - window.scrollY) / 4, 0),
        height
      )
      /* eslint-enable  no-magic-numbers */
      if (
        Math.round(currentHeight) !==
        Math.min(
          Math.max(Math.round(this.openHoursCollapsible.offsetHeight), 0),
          height
        )
      ) {
        this.openHoursCollapsible.style.height = `${currentHeight}px`
      }
    }
  }

  onScroll = () => {
    if (this.wrapper) {
      const isAtTop = this.wrapper.getBoundingClientRect().top <= 0
      if (isAtTop !== this.state.sticky) {
        this.setState({ sticky: isAtTop })
      }
    }
  }

  renderNavigationMenu() {
    const { centre, navigationMenu } = this.props

    if (navigationMenu) {
      return (
        <Fragment>
          {this.props.isCentreHome && (
            <div
              key="openHoursCollapsible"
              className={style.openHoursCollapsible}
              ref={ref => {
                this.openHoursCollapsible = ref
              }}
            >
              {centre.centreBanner && (
                <OpeningHoursBanner
                  text={centre.centreBanner.text}
                  hours={centre.centreBanner.hours}
                />
              )}
            </div>
          )}
          <Container
            className={classNames(style.container, {
              [style.hasNavigation]: navigationMenu,
            })}
            type="large"
          />
          <Menu navigationMenu={navigationMenu} />
        </Fragment>
      )
    }
    return null
  }

  render() {
    const { centre } = this.props
    return (
      <Fragment>
        <div ref={ref => (this.headerNormalPosition = ref)} />
        <header
          ref={ref => (this.wrapper = ref)}
          className={classNames(headerStyle, {
            [style.headerIsSticky]: this.state.sticky,
            [style.isCentreHome]: this.props.isCentreHome,
          })}
        >
          <div className={style.contentWrapper}>
            <Container className={style.container} type="large">
              <h1
                className={style.centreTitle}
                onTransitionEnd={this.setFixedHeight}
              >
                <Link
                  data-test-id="page-title"
                  to={`/${centre.slug}`}
                  title={`Go to ${centre.title} home`}
                  className={style.centreTitleLink}
                >
                  {centre.title}
                </Link>
              </h1>

              {centre.status === 'Open' && (
                <Fragment>
                  <Link
                    className={style.headerLink}
                    to={`/${centre.slug}/opening-hours`}
                    title="Centre trading hours"
                    data-test-id="centre-trading-hours"
                  >
                    <Icon type="hours" className={style.icon} />
                  </Link>
                  <Link
                    className={style.headerLink}
                    to={`/${centre.slug}/browse-stores`}
                    title="Browse stores"
                    data-test-id="browse-stores"
                  >
                    <Icon type="store" className={style.icon} />
                  </Link>
                  <ExternalLink
                    className={style.headerLink}
                    url={getGiftcardUrl(centre.country)}
                    eventCategory={analyticsUtils.categories.GIFTCARD_CLICK}
                    eventAction={analyticsUtils.actions.GIFTCARD_NAV_ICON_CLICK}
                    title="Gift Cards"
                    rel="noopener"
                    data-test-id="gift-cards"
                    target="_self"
                  >
                    <Icon type="gift-cards" className={style.icon} />
                  </ExternalLink>
                  <Search className={style.headerLink} centre={centre} />
                </Fragment>
              )}
            </Container>
            {this.renderNavigationMenu()}
          </div>
        </header>
      </Fragment>
    )
  }
}

Header.propTypes = {
  centre: CentreType,
  navigationMenu: NavigationMenuType,
  isCentreHome: PropTypes.bool,
}

export default withRouter(Header)
